import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

function RegularLineItemModal({ isOpen, onClose, onSave, initialItem }) {
    const [itemName, setItemName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState(0);
    const [isTaxable, setIsTaxable] = useState(true);

    useEffect(() => {
        if (isOpen && initialItem) {
            setItemName(initialItem.itemName || '');
            setDescription(initialItem.description || '');
            setPrice(parseFloat(initialItem.price) || 0);
            setIsTaxable(initialItem.isTaxable !== undefined ? !!initialItem.isTaxable : true);
        } else if (isOpen && !initialItem) {
            // Reset for new item
            setItemName('');
            setDescription('');
            setPrice(0);
            setIsTaxable(true);
        }
    }, [initialItem, isOpen]);

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            ['clean']
        ],
    };

    if (!isOpen) return null;

    const handleSave = () => {
        if (!itemName.trim()) {
            alert('Item name is required');
            return;
        }

        const lineItemData = {
            id: initialItem?.id, // Preserve ID if editing
            itemName: itemName.trim(),
            description: description,
            price: parseFloat(price) || 0,
            isTaxable: isTaxable,
            sortOrder: initialItem?.sortOrder
        };

        onSave(lineItemData);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {initialItem?.id ? 'Edit Line Item' : 'Add Line Item'}
                    </h2>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {/* Item Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Item Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={itemName}
                            onChange={(e) => setItemName(e.target.value)}
                            placeholder="e.g., Delivery Fee, Consultation"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description (Optional)
                        </label>
                        <ReactQuill
                            value={description}
                            onChange={setDescription}
                            modules={quillModules}
                            placeholder="Add any additional details about this item..."
                            className="bg-white"
                        />
                    </div>

                    {/* Price */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>
                    </div>

                    {/* Taxable Checkbox */}
                    <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-md">
                        <input
                            type="checkbox"
                            id="isTaxable"
                            checked={isTaxable}
                            onChange={(e) => setIsTaxable(e.target.checked)}
                            className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="isTaxable" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Apply tax to this item
                        </label>
                    </div>

                    {/* Price Summary */}
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Item Total:</span>
                            <span className="text-xl font-bold text-primary-600">
                                ${parseFloat(price || 0).toFixed(2)}
                            </span>
                        </div>
                        {isTaxable && (
                            <p className="text-xs text-gray-600 mt-1">Tax will be applied to this item</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end space-x-3">
                    <button
                        onClick={handleCancel}
                        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-100 transition duration-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-6 py-2 bg-primary-600 text-white rounded-md font-medium hover:bg-primary-700 transition duration-200 shadow-sm"
                    >
                        {initialItem?.id ? 'Update Item' : 'Add Item'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default RegularLineItemModal;
